import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, Check, X, Loader2 } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * CATEGORY MANAGER — Primary Category Selection
 * 
 * Per LOCKED DIRECTIVE:
 * - Users select exactly 3 primary categories
 * - These are always public (for discovery)
 * - Displayed as chips on Placard
 */
export default function CategoryManager() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [categoriesRes, userCatsRes] = await Promise.all([
        axios.get(`${API}/api/categories/main`, { headers }).catch(() => ({ data: { categories: [] } })),
        axios.get(`${API}/api/user/primary-categories`, { headers }).catch(() => ({ data: { primary_categories: [] } }))
      ]);
      
      setCategories(categoriesRes.data?.categories || []);
      setSelectedCategories((userCatsRes.data?.primary_categories || []).map(c => c.category_id || c.id));
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else if (prev.length < 3) {
        return [...prev, categoryId];
      } else {
        toast.info('Maximum 3 categories. Remove one first.');
        return prev;
      }
    });
  };

  const saveCategories = async () => {
    if (selectedCategories.length !== 3) {
      toast.error('Please select exactly 3 categories');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/user/primary-categories`, { 
        category_ids: selectedCategories 
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Categories saved');
      navigate('/settings/my-world');
    } catch (error) {
      toast.error('Failed to save categories');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--lk-sage)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/settings/my-world')} className="p-2 -ml-2 rounded-lg hover:opacity-70">
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
              </button>
              <h1 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Primary Categories</h1>
            </div>
            <Button 
              onClick={saveCategories} 
              disabled={saving || selectedCategories.length !== 3}
              style={{ backgroundColor: 'var(--lk-sage)' }}
              data-testid="save-categories-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Info */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 mt-0.5" style={{ color: 'var(--lk-sage)' }} />
            <div>
              <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>Select 3 categories</p>
              <p className="text-sm mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                These appear on your Placard and help others discover you. They&apos;re always public.
              </p>
            </div>
          </div>
        </Card>

        {/* Selected Preview */}
        <div>
          <p className="text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
            Selected ({selectedCategories.length}/3)
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedCategories.length === 0 ? (
              <span className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>None selected</span>
            ) : (
              selectedCategories.map(catId => {
                const cat = categories.find(c => c.id === catId || c.category_id === catId);
                return cat ? (
                  <button
                    key={catId}
                    onClick={() => toggleCategory(catId)}
                    className="px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5"
                    style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
                  >
                    {cat.name}
                    <X className="w-3 h-3" />
                  </button>
                ) : null;
              })
            )}
          </div>
        </div>

        {/* Category Grid */}
        <div className="space-y-2">
          <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>All Categories</p>
          <div className="grid grid-cols-1 gap-2">
            {categories.map(cat => {
              const catId = cat.id || cat.category_id;
              const isSelected = selectedCategories.includes(catId);
              
              return (
                <button
                  key={catId}
                  onClick={() => toggleCategory(catId)}
                  className={`p-4 rounded-xl flex items-center justify-between transition-colors`}
                  style={{ 
                    backgroundColor: isSelected ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                    color: isSelected ? 'white' : 'var(--fy-text-primary)'
                  }}
                  data-testid={`category-${catId}`}
                >
                  <span className="font-medium">{cat.name}</span>
                  {isSelected && <Check className="w-5 h-5" />}
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
